import {
  minutesToTimeSpan,
  timeSpanToMinutes,
} from "@/lib/utils/duration-format";
import type { PriceType } from "@/openapi/generated";

export const VARIANT_PARTICIPANT_COUNTS = [1, 2, 3] as const;

export type VariantParticipantCount =
  (typeof VARIANT_PARTICIPANT_COUNTS)[number];

export interface VariantColumnFormValues {
  participantCount: VariantParticipantCount;
  doNotUse: boolean;
  duration?: number;
  transitionMinutes: number;
  payRate?: number;
  price?: number;
  priceType?: PriceType;
  onlineMeetingUrl?: string | null;
}

export interface VariantColumnRequestInput {
  participantCount: VariantParticipantCount;
  doNotUse: boolean;
  duration: number;
  transitionMinutes: number;
  payRate: number;
  price: number;
  priceType: PriceType;
  onlineMeetingUrl?: string | null;
}

export interface AppointmentVariantWriteFields {
  variants: Array<{
    participantCount: VariantParticipantCount;
    duration: string;
    slotIntervalMinutes: number;
    price: number;
    priceType: PriceType;
    payRate: number;
    onlineMeetingUrl: string | null;
    isActive: true;
  }>;
  duration: string;
  slotIntervalMinutes: number;
  payRate: number;
  price: number;
  priceType: PriceType;
  onlineMeetingUrl: string | null;
  maxNumberOfStudents: null;
  appointmentTypeId: null;
}

interface VariantSource {
  participantCount?: unknown;
  duration?: unknown;
  slotIntervalMinutes?: unknown;
  price?: unknown;
  priceType?: PriceType | null;
  payRate?: unknown;
  onlineMeetingUrl?: string | null;
  isActive?: boolean | null;
}

const COLUMN_TITLES = [
  "Single Student",
  "Two Students",
  "Three Students",
] as const;
const OPTION_LABELS = [
  "Single Participant",
  "Two Participants",
  "Three Participants",
] as const;
const SHORT_OPTION_LABELS = ["Single", "Two", "Three"] as const;

export function variantColumnTitle(
  participantCount: VariantParticipantCount,
): string {
  return COLUMN_TITLES[participantCount - 1];
}

/** Label for a booked option. Null when the count is not 1, 2, or 3. */
export function participantOptionLabel(
  participantCount: number | null | undefined,
): string | null {
  if (
    participantCount === 1 ||
    participantCount === 2 ||
    participantCount === 3
  ) {
    return OPTION_LABELS[participantCount - 1];
  }
  return null;
}

export function formatActiveVariantOptions(
  variants:
    | Array<{ participantCount?: unknown; isActive?: boolean | null }>
    | null
    | undefined,
): string {
  const labels = (variants ?? [])
    .filter((variant) => variant.isActive !== false)
    .map((variant) => Number(variant.participantCount))
    .filter(
      (count): count is VariantParticipantCount =>
        count === 1 || count === 2 || count === 3,
    )
    .sort((a, b) => a - b)
    .map((count) => SHORT_OPTION_LABELS[count - 1]);

  return labels.length > 0 ? labels.join(", ") : "-";
}

export function formatClassroomOptionLabel(
  name: string,
  locationName: string | null | undefined,
): string {
  const room = name.trim();
  const location = locationName?.trim();
  if (!location) return room;
  return `${room} · ${location}`;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

export function emptyVariantColumn(
  participantCount: VariantParticipantCount,
  doNotUse = false,
): VariantColumnFormValues {
  return {
    participantCount,
    doNotUse,
    duration: undefined,
    transitionMinutes: 0,
    payRate: undefined,
    price: undefined,
    priceType: undefined,
    onlineMeetingUrl: "",
  };
}

export function defaultCreateVariantColumns(): [
  VariantColumnFormValues,
  VariantColumnFormValues,
  VariantColumnFormValues,
] {
  return [
    emptyVariantColumn(1, false),
    emptyVariantColumn(2, false),
    emptyVariantColumn(3, false),
  ];
}

export function variantsToFormColumns(
  variants: VariantSource[] | null | undefined,
): [VariantColumnFormValues, VariantColumnFormValues, VariantColumnFormValues] {
  return VARIANT_PARTICIPANT_COUNTS.map((participantCount) => {
    const match = (variants ?? []).find(
      (variant) => Number(variant.participantCount) === participantCount,
    );
    const doNotUse =
      participantCount === 1
        ? false
        : match == null || match.isActive === false;
    const duration = match
      ? timeSpanToMinutes(
          typeof match.duration === "string" ? match.duration : null,
        )
      : null;

    return {
      participantCount,
      doNotUse,
      duration: duration ?? undefined,
      transitionMinutes: match ? Number(match.slotIntervalMinutes ?? 0) : 0,
      payRate: match ? Number(match.payRate ?? 0) : undefined,
      price: match ? Number(match.price ?? 0) : undefined,
      priceType: match?.priceType ?? undefined,
      onlineMeetingUrl: match?.onlineMeetingUrl ?? "",
    };
  }) as [
    VariantColumnFormValues,
    VariantColumnFormValues,
    VariantColumnFormValues,
  ];
}

export function variantColumnsToRequest(
  columns: VariantColumnRequestInput[],
): AppointmentVariantWriteFields {
  const single = columns.find((column) => column.participantCount === 1);
  if (!single) {
    throw new Error("Single variant is required");
  }

  const active = columns
    .filter((column) => column.participantCount === 1 || !column.doNotUse)
    .sort((a, b) => a.participantCount - b.participantCount);

  const toVariant = (column: VariantColumnRequestInput) => ({
    participantCount: column.participantCount,
    duration: minutesToTimeSpan(column.duration) ?? "00:00:00",
    slotIntervalMinutes: column.transitionMinutes,
    price: column.price,
    priceType: column.priceType,
    payRate: column.payRate,
    onlineMeetingUrl: blankToNull(column.onlineMeetingUrl),
    isActive: true as const,
  });

  return {
    variants: active.map(toVariant),
    duration: minutesToTimeSpan(single.duration) ?? "00:00:00",
    slotIntervalMinutes: single.transitionMinutes,
    payRate: single.payRate,
    price: single.price,
    priceType: single.priceType,
    onlineMeetingUrl: blankToNull(single.onlineMeetingUrl),
    maxNumberOfStudents: null,
    appointmentTypeId: null,
  };
}

export function formColumnsToRequest(
  columns: VariantColumnFormValues[],
): AppointmentVariantWriteFields {
  return variantColumnsToRequest(
    columns.map((column) => ({
      participantCount: column.participantCount,
      doNotUse: column.participantCount === 1 ? false : column.doNotUse,
      duration: column.duration ?? 0,
      transitionMinutes: column.transitionMinutes,
      payRate: column.payRate ?? 0,
      price: column.price ?? 0,
      priceType: column.priceType as PriceType,
      onlineMeetingUrl: column.onlineMeetingUrl,
    })),
  );
}
